/** Comms vocabularies (server: nova/modules/comms). English source strings marked with msg(). */
import type { LucideIcon } from 'lucide-react';
import { Bell, Mail, MessageCircle, MessageSquare, Smartphone } from 'lucide-react';
import { msg, t } from '@/lib/i18n';
import type { Tone } from '@/ui';

export const CHANNEL: Record<string, { label: string; icon: LucideIcon }> = {
  inapp: { label: msg('In the app'), icon: MessageSquare },
  email: { label: msg('Email'), icon: Mail },
  sms: { label: msg('SMS'), icon: Smartphone },
  whatsapp: { label: msg('WhatsApp'), icon: MessageCircle },
  push: { label: msg('Push notification'), icon: Bell },
};

export const DELIVERY: Record<string, { label: string; tone: Tone }> = {
  queued: { label: msg('Queued'), tone: 'neutral' },
  scheduled: { label: msg('Waits for quiet hours to end'), tone: 'caution' },
  stored: { label: msg('Delivered in the app'), tone: 'good' },
  sent: { label: msg('Sent'), tone: 'good' },
  delivered: { label: msg('Delivered'), tone: 'good' },
  read: { label: msg('Read'), tone: 'good' },
  failed: { label: msg('Failed'), tone: 'bad' },
  received: { label: msg('Received'), tone: 'info' },
};

export const INTENT: Record<string, { label: string; tone: Tone }> = {
  confirm: { label: msg('Confirms'), tone: 'good' },
  decline: { label: msg('Declines'), tone: 'caution' },
  reschedule: { label: msg('Asks for another time'), tone: 'info' },
  question: { label: msg('Asks a question'), tone: 'accent' },
  opt_out: { label: msg('Stop messages'), tone: 'bad' },
  other: { label: msg('Unclear'), tone: 'neutral' },
};

export const TEMPLATE: Record<string, string> = {
  'coaching.invitation': msg('Invitation'),
  'coaching.confirmation': msg('Confirmation'),
  'coaching.reminder': msg('Reminder'),
  'coaching.reschedule': msg('New times offered'),
  'coaching.cancelled': msg('Cancellation'),
  'coaching.no_show': msg('Missed session'),
  'coaching.summary': msg('Session summary'),
  'coaching.quick_action': msg('Quick coaching message'),
  'driver.weekly_report': msg('Weekly report'),
  'alert.notification': msg('Notification'),
  'coaching.coach_attention': msg('Needs the coach'),
};

export const SUBJECT: Record<string, string> = {
  coaching_case: msg('Coaching'),
};

export function lbl(map: Record<string, { label: string } | string>, key: string | null | undefined): string {
  if (!key) return '';
  const v = map[key];
  if (!v) return key.replace(/[._]/g, ' ');
  return t(typeof v === 'string' ? v : v.label);
}

/** Kinds of notification (server: comms/notifications.py CATEGORIES), for preferences and flows. */
export const NOTIFY_CATEGORY: Record<string, { label: string; hint: string }> = {
  safety: { label: msg('Safety events'), hint: msg('Harsh driving, speeding, collision warnings, camera alerts, footage') },
  operations: { label: msg('Operations'), hint: msg('Places, arrivals, vehicle state, assets') },
  compliance: { label: msg('Compliance'), hint: msg('Driving hours, infringements, tachograph downloads and cards') },
  coaching: { label: msg('Coaching'), hint: msg('Coaching cases, sessions and driver replies') },
  insurance: { label: msg('Insurance'), hint: msg('Claims and their status') },
  data_quality: { label: msg('Data quality'), hint: msg('Data problems, device checks, installations') },
  automation: { label: msg('Automation tasks'), hint: msg('Tasks, approvals and failed automations') },
  reports: { label: msg('Reports'), hint: msg('Reports that are ready or shared') },
  system: { label: msg('System'), hint: msg('Account, users, integrations') },
  other: { label: msg('Other notifications'), hint: msg('Anything else automations send you') },
};

export const NOTIFY_SEVERITY: Record<string, { label: string; tone: Tone }> = {
  info: { label: msg('Information'), tone: 'neutral' },
  low: { label: msg('Low priority'), tone: 'neutral' },
  medium: { label: msg('Normal priority'), tone: 'info' },
  high: { label: msg('High priority'), tone: 'bad' },
};

export const DIGEST: Record<string, string> = {
  off: msg('Send each one at once'),
  hourly: msg('Once an hour'),
  daily: msg('Once a day'),
};

export const ACK_VIA: Record<string, string> = {
  inapp: msg('in the app'),
  link: msg('by link'),
  reply: msg('by reply'),
  agent: msg('through the assistant'),
};
